import {
  ExpensePolicyStatus,
  ExpenseReportStatus,
  ExpenseStatus,
  FilterCategoryEnum,
  FilterOption,
  StipendExpenseStatus,
  useFilterOptionsQuery,
} from "@common/utils/graphql";
import { useMemo, useState } from "react";
import { OptionsByCategoryAndCustomField } from "./types";
import { getCustomFieldQueryFilter } from "./utils";

export const useQueryFilterOptions = (
  searchText: string,
  categories: FilterCategoryEnum[],
) => {
  const { data } = useFilterOptionsQuery({
    variables: {
      searchText: searchText.length > 0 ? searchText : undefined,
      categories,
      // customFieldEntityIds,
    },
  });

  const optionsByCategoryAndCustomField = useMemo(() => {
    if (data?.filterOptions.__typename === "QueryFilterOptionsSuccess") {
      return data.filterOptions.data.options.reduce(
        (acc: OptionsByCategoryAndCustomField, option) => {
          if (option.category === FilterCategoryEnum.CustomField) {
            if (acc.optionsByCustomField[option.displaySubtitle]) {
              acc.optionsByCustomField[option.displaySubtitle].push(option);
            } else {
              acc.optionsByCustomField[option.displaySubtitle] = [option];
            }
          } else {
            if (acc.optionsByCategory[option.category]) {
              acc.optionsByCategory[option.category].push(option);
            } else {
              acc.optionsByCategory[option.category] = [option];
            }
          }
          return acc;
        },
        {
          optionsByCategory: {},
          optionsByCustomField: {},
        } as OptionsByCategoryAndCustomField,
      );
    }
    return {
      optionsByCategory: {},
      optionsByCustomField: {},
    } as OptionsByCategoryAndCustomField;
  }, [data]);

  return optionsByCategoryAndCustomField;
};

export const useFilterOptions = (defaultValue: FilterOption[] = []) => {
  const [value, setFilterOptions] = useState<FilterOption[]>(defaultValue);

  const onSelect = (option: FilterOption) => {
    const filteredOptions = value.filter(
      (o) => !(o.id == option.id && o.category == option.category),
    );
    if (value.length === filteredOptions.length) {
      setFilterOptions([...value, option]);
    } else {
      setFilterOptions(filteredOptions);
    }
  };

  const query = useMemo(() => {
    const departmentId = value
      .filter((f) => f.category === FilterCategoryEnum.Department)
      .map((f) => Number(f.id));
    const employeeId = value
      .filter((f) => f.category === FilterCategoryEnum.Employee)
      .map((f) => f.id);
    const merchantName = value
      .filter((f) => f.category === FilterCategoryEnum.Merchant)
      .map((f) => f.id);
    const expenseCategoryGLId = value
      .filter((f) => f.category === FilterCategoryEnum.ExpenseCategory)
      .map((f) => Number(f.id));
    const expenseStatus = value
      .filter((f) => f.category === FilterCategoryEnum.ExpenseStatus)
      .map((f) => f.id as ExpenseStatus);
    const expenseReportStatus = value
      .filter((f) => f.category === FilterCategoryEnum.ReportStatus)
      .map((f) => f.id as ExpenseReportStatus);
    const reviewerEmployeeId = value
      .filter((f) => f.category === FilterCategoryEnum.Reviewer)
      .map((f) => f.id);
    const policyStatus = value
      .filter((f) => f.category === FilterCategoryEnum.PolicyStatus)
      .map((f) => f.id as ExpensePolicyStatus);
    const stipendExpenseStatus = value
      .filter((f) => f.category === FilterCategoryEnum.StipendExpenseStatus)
      .map((f) => f.id as StipendExpenseStatus);
    const stipendRuleId = value
      .filter((f) => f.category === FilterCategoryEnum.StipendRule)
      .map((f) => Number(f.id));
    const customField = getCustomFieldQueryFilter(value);
    // TODO: Add payment method

    return {
      departmentId: departmentId.length ? departmentId : undefined,
      employeeId: employeeId.length ? employeeId : undefined,
      merchantName: merchantName.length ? merchantName : undefined,
      expenseCategoryGLId: expenseCategoryGLId.length
        ? expenseCategoryGLId
        : undefined,
      expenseStatus: expenseStatus.length ? expenseStatus : undefined,
      expenseReportStatus: expenseReportStatus.length
        ? expenseReportStatus
        : undefined,
      reviewerEmployeeId: reviewerEmployeeId.length
        ? reviewerEmployeeId
        : undefined,
      policyStatus: policyStatus.length ? policyStatus : undefined,
      stipendExpenseStatus: stipendExpenseStatus.length
        ? stipendExpenseStatus
        : undefined,
      stipendRuleId: stipendRuleId.length ? stipendRuleId : undefined,
      customField,
    };
  }, [value]);

  return {
    value,
    onSelect,
    query,
  };
};
